import {
  APPLY_ABANDONED,
  ENGINE_APPLYING,
  type EngineApplyAttempt,
  type EngineApplyVerification,
  type EngineArtifactRecord,
  type EngineRollbackAcknowledgement,
  type EngineTaskReport,
} from "@/services/migrations/engine/types";

/**
 * The gate logic for the engine's write path, kept out of the components so the rules
 * that decide whether a human may authorise a change to a customer's org can be tested
 * directly rather than inferred from a `disabled` attribute.
 *
 * WHAT THE BACKEND ALREADY ENFORCES, and why this file exists anyway.
 *
 * `apply.py::_verify` re-checks five factors on every request and refuses the whole
 * write if any falls short: the apply flag, an explicit `op`, `check_only` as the
 * literal `false`, a verbatim confirmation token naming (org, component, compile_key),
 * and a recorded GATE_APPLY approval whose `compile_key` equals the artifact's. This
 * module does NOT re-implement that gate — it cannot, and a client-side gate that
 * believed it could would be the most dangerous code in the app.
 *
 * It exists because REFUSAL IS NOT INFORMED CONSENT. The server can stop a bad write; it
 * cannot stop a human approving something they never looked at, and an approval given
 * blind is a valid approval by every check the server makes. So the rules here are about
 * what the SCREEN must have shown before an approval is offered:
 *
 *   1. The artifact list has loaded, and there is an artifact.
 *   2. The reviewer has the artifact body OPEN — not a summary of it, the bytes.
 *   3. The version on screen is the CURRENT one, not a superseded revision.
 *   4. The `compile_key` of the artifact on screen equals the task's, so the approval
 *      being recorded names the artifact that was read.
 *
 * Rule 4 is the one that carries the whole design. `compile_key` is a hash over every
 * input that can change a byte, so an artifact that recompiles gets a new one and both
 * the recorded approval AND the confirmation token stop naming it — at the same instant,
 * by construction. An approval recorded against an earlier compile is not stale in a way
 * anyone notices by eye: the ledger still reads "approved by <name>". The only defence
 * is to never record one in the first place, which is what these blockers do.
 */

export type ApprovalBlockerCode =
  /** The artifacts request has not resolved, or failed. Nothing can be vouched for. */
  | "ARTIFACT_NOT_LOADED"
  /** This task compiled nothing. There is no artifact for an approval to name. */
  | "NO_ARTIFACT"
  /** The reviewer has not opened the body. A file list is not the file. */
  | "ARTIFACT_NOT_DISPLAYED"
  /** The open version has been replaced. Approving would name bytes nobody read. */
  | "DISPLAYED_ARTIFACT_SUPERSEDED"
  /** The artifact recompiled under the reviewer. The approval would be void on arrival. */
  | "COMPILE_KEY_MISMATCH";

export interface ApprovalBlocker {
  code: ApprovalBlockerCode;
  message: string;
}

/**
 * Something the reviewer must SEE, that does not disable the decision.
 *
 * Distinct from a blocker on purpose. A blocker says "this screen cannot support an
 * approval". A warning says "this screen supports an approval and here is what you are
 * approving" — and the difference matters most for advisories, which describe behaviour
 * that was CARRIED and that the target performs differently. Nothing was lost, so no
 * coverage number moves, and no gate can refuse on it. It is still the thing a human at
 * the gate most needs to be told, so it is raised here rather than left in a panel the
 * reader might not open.
 */
export type ApprovalWarningCode =
  | "SILENT_LOSS"
  | "ADVISORY"
  | "SOURCE_PARTIAL"
  | "GROUNDING_INCOMPLETE"
  | "RESIDUE"
  /** Carried, but at less strength than the source demanded. No coverage number shows it. */
  | "WEAKENED"
  | "VALIDATION_FAILED"
  | "UNVALIDATED";

export interface ApprovalWarning {
  code: ApprovalWarningCode;
  message: string;
}

/** What the screen is currently showing, as far as the approval gate is concerned. */
export interface DisplayedArtifact {
  component_key: string;
  version: number;
  compile_key: string;
  superseded: boolean;
}

export interface ApprovalGateInput {
  task: EngineTaskReport;
  /** `false` while the artifacts query is pending or errored. */
  artifactsLoaded: boolean;
  artifacts: EngineArtifactRecord[];
  /** The artifact whose BODY is open on screen, or `null` when none is. */
  displayed: DisplayedArtifact | null;
  /** Plan-wide silent loss, from `coverage.planSilentLoss`. */
  silentLoss: boolean;
}

export interface ApprovalGateState {
  blockers: ApprovalBlocker[];
  warnings: ApprovalWarning[];
  /** Approve/Reject may be offered. `false` disables the affordance, not just its tone. */
  canDecide: boolean;
}

export function approvalGateState(input: ApprovalGateInput): ApprovalGateState {
  const { task, artifacts, artifactsLoaded, displayed, silentLoss } = input;
  const blockers: ApprovalBlocker[] = [];

  if (!artifactsLoaded) {
    blockers.push({
      code: "ARTIFACT_NOT_LOADED",
      message:
        "The artifacts for this task have not loaded. Nothing can be approved from a screen that cannot show what it is approving.",
    });
  } else if (artifacts.length === 0) {
    blockers.push({
      code: "NO_ARTIFACT",
      message:
        "This task compiled no artifact, so there is nothing for an approval to name. Approving would record a decision about nothing.",
    });
  } else if (!displayed) {
    blockers.push({
      code: "ARTIFACT_NOT_DISPLAYED",
      message:
        "Open the artifact below. An approval is a statement that you read these bytes, and a file list is not the file.",
    });
  } else {
    if (displayed.superseded || !isCurrentVersion(displayed, artifacts)) {
      blockers.push({
        code: "DISPLAYED_ARTIFACT_SUPERSEDED",
        message: `You are reading version ${displayed.version} of ${displayed.component_key}, which has been replaced. Open the current version before deciding.`,
      });
    }
    if (task.compile_key && displayed.compile_key && displayed.compile_key !== task.compile_key) {
      blockers.push({
        code: "COMPILE_KEY_MISMATCH",
        message:
          "This artifact recompiled while you were reading it. The compile key on screen no longer matches the task's, so an approval recorded now would name an artifact nobody reviewed — and the write path would refuse it. Reload the report.",
      });
    }
  }

  return { blockers, warnings: approvalWarnings(task, silentLoss), canDecide: blockers.length === 0 };
}

/** The current version of the displayed component, by the version list rather than a flag. */
function isCurrentVersion(displayed: DisplayedArtifact, artifacts: EngineArtifactRecord[]): boolean {
  const siblings = artifacts.filter(
    (record) => record.component_key === displayed.component_key
  );
  if (siblings.length === 0) return false;
  const highest = Math.max(...siblings.map((record) => record.version));
  return displayed.version === highest;
}

export function approvalWarnings(task: EngineTaskReport, silentLoss: boolean): ApprovalWarning[] {
  const warnings: ApprovalWarning[] = [];
  if (silentLoss) {
    warnings.push({
      code: "SILENT_LOSS",
      message:
        "This plan dropped effects with no declared reason. Nothing says what was lost or how to recover it, and approving carries that forward.",
    });
  }
  for (const advisory of task.advisories) {
    warnings.push({
      code: "ADVISORY",
      // The engine's own sentence, verbatim. Paraphrasing the one thing a coverage
      // number cannot show would be the place to get it subtly wrong.
      message: `${advisory.code}: ${advisory.detail}`,
    });
  }
  if (!task.source_complete) {
    warnings.push({
      code: "SOURCE_PARTIAL",
      message:
        "The source could not be read in full. Every coverage number on this task is measured against a partial read.",
    });
  }
  if (task.menus_unavailable.length > 0) {
    warnings.push({
      code: "GROUNDING_INCOMPLETE",
      message: `${task.menus_unavailable.length} grounding arm(s) did not answer (${task.menus_unavailable.join(", ")}). Coverage is inflated by exactly that much.`,
    });
  }
  if (task.residue.length > 0) {
    warnings.push({
      code: "RESIDUE",
      message: `${task.residue.length} manual step(s) remain after this migration. They are not optional and nothing else will do them.`,
    });
  }
  // A REPORTED positive count only. Weakened effects are declared — they carry
  // `CONTRACT_EFFECT_WEAKENED` and a reason naming the missing grants — so this is not
  // silent loss and does not suppress success; but the target will perform them
  // differently from the source, and the coverage fraction cannot show it because a
  // weakened effect is not in its numerator. An UNKNOWN count deliberately raises nothing:
  // the report does not carry this count today, so warning on absence would put an
  // unactionable line on every approval on the platform.
  if (task.weakened_count > 0) {
    warnings.push({
      code: "WEAKENED",
      message: `${task.weakened_count} effect(s) are carried at reduced strength — the target performs them, but grants less than the source did. Each one names exactly which grants are missing.`,
    });
  }
  if (task.validation_outcome === "validation_failed") {
    warnings.push({
      code: "VALIDATION_FAILED",
      message: "The org rejected this artifact in a check-only validation.",
    });
  } else if (task.validation_outcome === "unvalidated") {
    warnings.push({
      code: "UNVALIDATED",
      message:
        "This artifact was never checked against the org. Unvalidated is not the same as valid.",
    });
  }
  return warnings;
}

// ── the write itself ────────────────────────────────────────────────────────────

export type ApplyBlockerCode =
  | "APPLY_DISABLED"
  | "NOTHING_TO_CREATE"
  | "NOT_APPROVED"
  | "APPROVAL_STALE"
  | "NO_TOKEN"
  | "TOKEN_MISMATCH"
  | "ALREADY_APPLIED"
  | "ATTEMPT_PARKED";

export interface ApplyBlocker {
  code: ApplyBlockerCode;
  message: string;
}

/**
 * Why this task cannot be written to the org right now, said BEFORE the request.
 *
 * A mirror of `apply.py::_verify`, and deliberately only a mirror: the server checks
 * every one of these again and its answer is the only one that decides anything. The
 * value of saying it first is that a refusal a caller could have predicted is a refusal
 * that should never have been sent — a round trip that ends in "you have no approval"
 * teaches nothing that the screen could not have said while the button was still grey.
 */
export function applyBlockers(task: EngineTaskReport, typedToken: string): ApplyBlocker[] {
  const apply = task.apply;
  const blockers: ApplyBlocker[] = [];

  if (!apply.enabled) {
    blockers.push({
      code: "APPLY_DISABLED",
      message:
        "The write path is off in this environment. Enabling the engine authorises compiling and check-only validation; writing to an org is a separate flag and a separate decision.",
    });
  }
  if (apply.will_create.length === 0) {
    blockers.push({
      code: "NOTHING_TO_CREATE",
      message: "This task has no artifact to apply.",
    });
  }
  if (apply.wrote_to_org) {
    blockers.push({
      code: "ALREADY_APPLIED",
      message:
        "A component of this task already exists in the org because of this plan. This phase creates and never overwrites.",
    });
  }
  if (task.status === ENGINE_APPLYING) {
    blockers.push({
      code: "ATTEMPT_PARKED",
      message:
        "An apply attempt on this task never settled, so the task is parked and nothing may re-submit. Whether the org changed is exactly what is unknown.",
    });
  }
  if (!apply.approved) {
    blockers.push({
      code: "NOT_APPROVED",
      message:
        "No recorded GATE_APPLY approval names this task. The gate is a ledger row written by a human, not a flag on a request.",
    });
  } else if (!apply.approval_matches_artifact) {
    blockers.push({
      code: "APPROVAL_STALE",
      message:
        "The recorded approval names a different compile key than this artifact. The artifact recompiled after it was approved, so the approval does not transfer — re-approve the current one.",
    });
  }
  if (!apply.confirmation_token) {
    blockers.push({
      code: "NO_TOKEN",
      message: "The engine did not issue a confirmation token for this artifact.",
    });
  } else if (typedToken !== apply.confirmation_token) {
    blockers.push({
      code: "TOKEN_MISMATCH",
      message:
        "Type the confirmation token exactly. It names the org, the component and the full compile key, so a token for another component — or for an earlier compile of this one — cannot authorise this write.",
    });
  }
  return blockers;
}

// ── the post-deploy read-back, and the limits of it ─────────────────────────────

export type VerificationStateCode =
  /** No read-back is recorded on this attempt at all. */
  | "NOT_RECORDED"
  /** The component could not be read back. Not a failure and not a pass. */
  | "NOT_READ"
  /** Read and compared in full. `identical` here really does mean everything matched. */
  | "VERIFIED_WHOLE"
  /** Read and compared, but some children of this artifact could not be read at all. */
  | "VERIFIED_PARTIAL"
  /** Read and compared, and what is in the org is not what we compiled. */
  | "MISMATCH";

export interface VerificationState {
  code: VerificationStateCode;
  tone: "success" | "warm" | "destructive" | "neutral";
  /** The headline. Never says "everything matched" unless that is what happened. */
  headline: string;
  /** The surfaces the read-back could not compare. Rendered, never summarised away. */
  unreadSurfaces: string[];
  /** The engine's own sentence, verbatim where it sent one. */
  detail: string;
}

/**
 * What a post-apply verification ACTUALLY establishes.
 *
 * THE DISTINCTION THIS EXISTS FOR. `unread_surfaces` lists the children of this artifact
 * that the org exposes no readable row for — `recordTypeVisibilities` (no org-side table
 * exists; three candidate sObjects are all 404 on a live describe) and
 * `applicationVisibilities` (measured live: 21 of 40 ids resolved, so reading it would
 * report half its members as dropped grants). They are scoped OUT of the diff, which is
 * correct — a surface nobody could read must never be counted as a surface the org dropped
 * — and it means a clean diff carrying unread surfaces says "everything we could read
 * matched", not "everything matched".
 *
 * Without this field on screen, `identical` reads as the second sentence. Empty
 * `unread_surfaces` is the only case an applied outcome may be read as "the org contains
 * what we intended" without a caveat, and that is exactly why the caveat stays meaningful
 * when it does appear.
 */
export function verificationState(verification: EngineApplyVerification): VerificationState {
  if (!verification.present) {
    return {
      code: "NOT_RECORDED",
      tone: "neutral",
      headline: "No read-back is recorded for this attempt.",
      unreadSurfaces: [],
      detail:
        "Nothing here says what the org contains. That is not a claim that it is empty or that it matches — it is the absence of the check.",
    };
  }
  if (!verification.read) {
    return {
      code: "NOT_READ",
      tone: "destructive",
      headline: "The component could not be read back from the org.",
      unreadSurfaces: [],
      detail:
        verification.message ||
        "The deploy succeeded and the component could not be read back, so what the org now contains is unverified. It is not a failure and it is not a pass: a component was created and nobody has looked at it.",
    };
  }
  const unread = verification.unread_surfaces;
  const identical = verification.diff.identical === true && !verification.flow_is_active;
  if (!identical) {
    return {
      code: "MISMATCH",
      tone: "destructive",
      headline: "What is in the org is not what was compiled.",
      unreadSurfaces: unread,
      detail: verification.message,
    };
  }
  if (unread.length > 0) {
    return {
      code: "VERIFIED_PARTIAL",
      tone: "warm",
      // The sentence a reader must not be able to shorten to "it matched".
      headline: `Everything that could be READ matched — ${unread.length} surface(s) were never compared.`,
      unreadSurfaces: unread,
      detail: verification.message,
    };
  }
  return {
    code: "VERIFIED_WHOLE",
    tone: "success",
    headline: "The org contains what was compiled, compared end to end.",
    unreadSurfaces: [],
    detail: verification.message,
  };
}

/**
 * WHICH acknowledgement a rollback of this attempt would require — and they are not
 * interchangeable.
 *
 *   `acknowledge_unverified`   the post-apply read never succeeded. There is no diff, so
 *                              there is nothing for a person to have "seen and accepted".
 *   `acknowledge_mismatch`     the apply settled with a diff. A person has looked at it.
 *   `null`                     the apply verified clean; neither flag is needed.
 *
 * Provenance is what licenses the delete — the pre-deploy probe said ABSENT and our deploy
 * carries an id — and neither of these flags touches that. They acknowledge a fact about
 * CONTENT, and the whole content of an acknowledgement is that it names the fact being
 * acknowledged. Sending `acknowledge_mismatch` for an unverified apply would assert
 * somebody had read a diff that does not exist, and the server refuses it precisely so
 * that assertion cannot be made by accident.
 *
 * The order matters: the unverified case is checked FIRST, because an attempt that was
 * never read back also has no settled comparison to be a mismatch about.
 */
export function rollbackAcknowledgement(
  attempt: EngineApplyAttempt
): EngineRollbackAcknowledgement {
  const verification = attempt.verification;
  if (!verification.present || !verification.read) return "acknowledge_unverified";
  if (verification.diff.identical === true && !verification.flow_is_active) return null;
  return "acknowledge_mismatch";
}

/** One sentence naming what a rollback of this attempt would be acknowledging. */
export function rollbackAcknowledgementReason(
  acknowledgement: EngineRollbackAcknowledgement
): string | null {
  if (acknowledgement === "acknowledge_unverified") {
    return "Deleting this would require acknowledging that nobody looked: the component was never read back, so what is in the org was never compared with what was compiled. It IS ours — the org did not have it before we deployed and our deploy succeeded — so it can be deleted, but not by accident.";
  }
  if (acknowledgement === "acknowledge_mismatch") {
    return "Deleting this would require acknowledging the mismatch: the component in the org is not exactly what was compiled, and a person has to have seen that diff and accepted it. That is a different statement from 'nobody looked', and the two are not interchangeable.";
  }
  return null;
}

// ── the parked-attempt escape hatch ─────────────────────────────────────────────

/**
 * The open attempt a `clear-apply` would close, or `null`.
 *
 * `clear_stuck_apply` refuses unless the task is at `applying`, carries an open attempt,
 * and that attempt has NO deploy id — an attempt with a deploy id is not stuck, it has a
 * Salesforce job that can be polled, and the right move there is to apply again and
 * resume it rather than to abandon it.
 */
export function parkedApplyAttempt(task: EngineTaskReport): EngineApplyAttempt | null {
  if (task.status !== ENGINE_APPLYING) return null;
  const open = task.apply.attempts.filter(
    (attempt) => attempt.kind === "apply" && (!attempt.state || attempt.state === "open")
  );
  const attempt = open[open.length - 1];
  if (!attempt) return null;
  if (attempt.deploy_id) return null;
  return attempt;
}

/** Why the escape hatch is not offered, when the task looks parked but is not clearable. */
export function clearApplyUnavailableReason(task: EngineTaskReport): string | null {
  if (task.status !== ENGINE_APPLYING) return null;
  const open = task.apply.attempts.filter(
    (attempt) => attempt.kind === "apply" && (!attempt.state || attempt.state === "open")
  );
  const attempt = open[open.length - 1];
  if (!attempt) {
    return "This task is parked but carries no open apply attempt, so there is no attempt to close. This is a state to look at directly.";
  }
  if (attempt.deploy_id) {
    return `This attempt carries deploy id ${attempt.deploy_id}, so it is not stuck — it has a Salesforce job that can be polled. Applying again resumes it.`;
  }
  return null;
}

/**
 * The token `clear-apply` will require, derived the way the server derives it.
 *
 * DERIVED, NOT RECEIVED — and labelled as such wherever it is shown. `/report` renders
 * `apply.confirmation_token` for the write path but issues nothing for this one, and the
 * attempt row does not carry its own `compile_key`. A parked task cannot recompile while
 * it is parked, so the task's key is the attempt's key; if that assumption is ever wrong
 * the server refuses with `CONFIRMATION_MISMATCH` and returns the token it wanted, which
 * is then what the screen shows.
 */
export function derivedClearApplyToken(
  orgId: string,
  componentKey: string,
  compileKey: string
): string {
  if (!orgId || !componentKey || !compileKey) return "";
  return `ABANDON APPLY OF ${componentKey} IN ${orgId} ${compileKey}`;
}

/** `clear_stuck_apply` requires at least 10 characters, and it goes in the ledger verbatim. */
export const CLEAR_APPLY_MIN_REASON = 10;

export function clearApplyReasonValid(reason: string): boolean {
  return reason.trim().length >= CLEAR_APPLY_MIN_REASON;
}

/**
 * `APPLY_ABANDONED` is not "nothing happened". Rendered wherever the outcome appears,
 * because the one thing this state does NOT establish is whether the org changed.
 */
export function abandonedNotice(outcome: string): string | null {
  if (outcome !== APPLY_ABANDONED) return null;
  return "This attempt was closed without a verdict from the org. It does NOT mean nothing was deployed — it means a human checked and took responsibility for the answer. The task is blocked, not done.";
}

// ── artifact versions ───────────────────────────────────────────────────────────

export interface ArtifactChainEntry {
  record: EngineArtifactRecord;
  /**
   * The version that replaced this one. DERIVED from the version list rather than read
   * off the wire: there is no `superseded_by` field, and a client-maintained second
   * opinion about which version supersedes which is a second thing that can disagree
   * with the list it was computed from.
   */
  superseded_by: number | null;
  current: boolean;
}

export interface ArtifactChain {
  component_key: string;
  /** Highest version first — the current one leads, its history follows. */
  entries: ArtifactChainEntry[];
  current: EngineArtifactRecord | null;
}

/**
 * Group a task's artifact rows into one chain per component, newest first.
 *
 * Artifacts are append-only: a re-compile with different inputs makes version N+1 and
 * supersedes N rather than overwriting it, so every byte that was ever compiled stays
 * retrievable. Showing only the current version would hide that a component was
 * recompiled at all — which is exactly the event that voids an approval.
 */
export function artifactChains(records: EngineArtifactRecord[]): ArtifactChain[] {
  const byComponent = new Map<string, EngineArtifactRecord[]>();
  for (const record of records) {
    const bucket = byComponent.get(record.component_key);
    if (bucket) bucket.push(record);
    else byComponent.set(record.component_key, [record]);
  }
  return [...byComponent.entries()].map(([component_key, rows]) => {
    const sorted = [...rows].sort((a, b) => b.version - a.version);
    const highest = sorted[0]?.version ?? 0;
    const entries = sorted.map((record, index) => ({
      record,
      // The next-higher version present in the list. `null` on the newest row.
      superseded_by: index === 0 ? null : sorted[index - 1].version,
      current: record.version === highest && !record.superseded,
    }));
    return {
      component_key,
      entries,
      current: entries.find((entry) => entry.current)?.record ?? null,
    };
  });
}
